import { Drawer } from 'antd';
import { CloseOutlined, LogoutOutlined, RightOutlined } from '@ant-design/icons';
import { Link, useLocation } from 'react-router-dom';
import type { TUser } from '@/services/auth/auth.types.ts';
import { AppLogo } from '@/icons/AppLogo.tsx';
import { isNavItemActive, type TNavItem } from '@/components/Layouts/navigation.tsx';
import { roleLabel, useLogout, userFullName } from '@/components/Layouts/account.ts';
import { Avatar } from '@/components/Layouts/Avatar.tsx';

interface IMobileMenu {
  open: boolean;
  onClose: () => void;
  items: TNavItem[];
  user?: TUser;
}

/** Full-height sheet that slides in from the right on phones. */
export const MobileMenu = ({ open, onClose, items, user }: IMobileMenu) => {
  const { pathname } = useLocation();
  const { mutate: logout, isPending } = useLogout();

  const subAccount = user?.sub_account;
  const isClient = user?.role === 'CLIENT';

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement='right'
      width='min(340px, 88vw)'
      closable={false}
      rootClassName='mobile-menu'
      styles={{
        body: { padding: 0, display: 'flex', flexDirection: 'column' },
        header: { display: 'none' },
      }}
    >
      <div className='mobile-menu__top'>
        <AppLogo cropped width={58} height={42} />
        <button
          type='button'
          className='mobile-menu__close'
          onClick={onClose}
          aria-label='Close menu'
        >
          <CloseOutlined />
        </button>
      </div>

      {isClient ? (
        <Link to='/client/profile' className='mobile-menu__account' onClick={onClose}>
          <Avatar user={user} size='md' />
          <span className='mobile-menu__account-text'>
            <span className='mobile-menu__account-name'>{userFullName(user)}</span>
            <span className='mobile-menu__account-meta'>View profile</span>
          </span>
          <RightOutlined className='mobile-menu__account-chevron' />
        </Link>
      ) : (
        <div className='mobile-menu__account'>
          <Avatar user={user} size='md' />
          <span className='mobile-menu__account-text'>
            <span className='mobile-menu__account-name'>{userFullName(user)}</span>
            <span className='mobile-menu__account-meta'>{roleLabel(user?.role)}</span>
          </span>
        </div>
      )}

      {subAccount && (
        <div className='mobile-menu__sub-account'>
          Viewing as <strong>{subAccount.name}</strong>
          <span className='mobile-menu__sub-account-mode'>{subAccount.behaviour}</span>
        </div>
      )}

      <nav className='mobile-menu__nav' aria-label='Primary'>
        {items.map((item) => {
          const active = isNavItemActive(item, pathname);
          return (
            <Link
              key={item.key}
              to={item.key}
              onClick={onClose}
              className={`mobile-menu__link${active ? ' is-active' : ''}`}
              aria-current={active ? 'page' : undefined}
            >
              <span className='mobile-menu__icon'>{item.icon}</span>
              {item.label}
            </Link>
          );
        })}
      </nav>

      <div className='mobile-menu__footer'>
        <button
          type='button'
          className='mobile-menu__logout'
          disabled={isPending}
          onClick={() => {
            onClose();
            logout();
          }}
        >
          <span className='mobile-menu__icon'>
            <LogoutOutlined />
          </span>
          Log out
        </button>
      </div>
    </Drawer>
  );
};
